import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { Input, Typography, theme as antdTheme } from 'antd';
import {
  typeScale,
  space,
  tracking,
  usePrefersReducedMotion,
  PanelToggle,
  PANEL_COLLAPSED_WIDTH,
  PANEL_TRANSITION_MS,
} from '@viq-admin/ui';
import type { BatchConference } from './types';
import { EventCard, type EventGroup } from './EventCard';
import { groupOf } from './eventGrouping';

interface Props {
  conferences: BatchConference[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  collapsed: boolean;
  onToggleCollapsed: () => void;
  /**
   * Per-conference "worth a look" badge count (p3 Task 6 — the CRM merge
   * queue; reworded p3 Task 8, which dropped the blocking framing). Absent →
   * no card renders a badge, so a shell that never passes this (the console,
   * or the portal without a CRM connection) is unchanged.
   */
  badgeByConferenceId?: Record<string, number>;
  /**
   * The badge's sentence, when the shell counts something other than the
   * legacy terms (the unified CRM grid, port plan D9). Absent → today's
   * `CRM_COPY.rail.badgeTooltip`.
   */
  badgeTooltip?: (n: number) => string;
  /**
   * Phone layout: the open rail floats over the page (behind a mask that closes
   * it) instead of pushing the page aside, since at 320px it would leave the
   * page a sliver. Collapsed, it is the same 48px strip either way.
   */
  overlay?: boolean;
}

const ORDERED_GROUPS: EventGroup[] = ['active', 'upcoming', 'past'];

const RAIL_WIDTH = 320;
/** Above the leads table's sticky header and the stage strip (zIndex 4). */
const OVERLAY_Z_INDEX = 20;

/**
 * Event-selection rail — the left panel of the two-panel Batch layout. Ported
 * from the portal's EventRail: events are grouped into ACTIVE / UPCOMING / PAST
 * by their date range (active by start desc, upcoming by start asc, past by end
 * desc), each group rendered under a header. Search matches name or location.
 *
 * A full-height column: its left edge is the IQ Panel's (the Batch screen puts
 * it flush against it), its right edge is its own divider, kept in both states
 * so the collapsed strip still reads as a panel rather than a stray button.
 */
export function EventRail({
  conferences,
  selectedId,
  onSelect,
  collapsed,
  onToggleCollapsed,
  badgeByConferenceId,
  badgeTooltip,
  overlay = false,
}: Props) {
  const { token } = antdTheme.useToken();
  const [query, setQuery] = useState('');
  const reducedMotion = usePrefersReducedMotion();

  /**
   * Holds the rail's content in the DOM through the closing slide. Dropping it
   * the instant the toggle is hit would blank the panel and THEN slide it shut
   * — the abrupt effect this animation exists to remove. Held, it travels out
   * of view behind the clipping edge instead.
   *
   * Never set for a rail that starts collapsed, so a collapsed rail still
   * renders only its toggle.
   */
  const [contentMounted, setContentMounted] = useState(!collapsed);

  useEffect(() => {
    if (!collapsed) {
      setContentMounted(true);
      return;
    }
    if (!contentMounted) return;
    if (reducedMotion) {
      setContentMounted(false);
      return;
    }
    const timer = setTimeout(() => setContentMounted(false), PANEL_TRANSITION_MS);
    return () => clearTimeout(timer);
  }, [collapsed, contentMounted, reducedMotion]);

  // Shown immediately on the way in; held (above) on the way out.
  const showContent = !collapsed || contentMounted;

  const fade: CSSProperties = {
    opacity: collapsed ? 0 : 1,
    transition: reducedMotion ? 'none' : `opacity ${PANEL_TRANSITION_MS}ms ease-in-out`,
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return conferences.filter(
      (c) =>
        !q ||
        c.name.toLowerCase().includes(q) ||
        (c.location ?? '').toLowerCase().includes(q),
    );
  }, [conferences, query]);

  const groups: Record<EventGroup, BatchConference[]> = useMemo(() => {
    const g: Record<EventGroup, BatchConference[]> = { active: [], upcoming: [], past: [] };
    for (const c of filtered) g[groupOf(c)].push(c);
    // active by start desc, upcoming by start asc, past by end desc.
    g.active.sort((a, b) => (b.startDate ?? '').localeCompare(a.startDate ?? ''));
    g.upcoming.sort((a, b) => (a.startDate ?? '').localeCompare(b.startDate ?? ''));
    g.past.sort((a, b) => (b.endDate ?? '').localeCompare(a.endDate ?? ''));
    return g;
  }, [filtered]);

  const panel: CSSProperties = {
    width: collapsed ? PANEL_COLLAPSED_WIDTH : RAIL_WIDTH,
    // Explicit, so the border sits OUTSIDE those widths: the collapsed
    // strip keeps a full 48px for its toggle, matching the IQ Panel.
    boxSizing: 'content-box',
    borderRight: `1px solid ${token.colorBorderSecondary}`,
    height: '100%',
    flexShrink: 0,
    overflow: 'hidden',
    transition: reducedMotion ? 'none' : `width ${PANEL_TRANSITION_MS}ms ease-in-out`,
  };

  const rail = (
    // One container across both states, so its width is something CSS can
    // transition. `overflow: hidden` clips the full-width content as it
    // narrows, rather than letting it reflow into a 48px column on the way.
    <div
      data-testid="event-rail"
      style={
        overlay
          ? {
              ...panel,
              position: 'absolute',
              top: 0,
              left: 0,
              bottom: 0,
              // Never wider than the phone, less a strip of page to tap.
              maxWidth: `calc(100vw - ${PANEL_COLLAPSED_WIDTH}px)`,
              background: token.colorBgLayout,
              boxShadow: collapsed ? undefined : token.boxShadowSecondary,
              zIndex: OVERLAY_Z_INDEX,
            }
          : panel
      }
    >
      {/* Laid out at full width in both states; the container clips it. */}
      <div style={{ width: RAIL_WIDTH, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
          {/* One persistent element in the rail's top-left square — the part
              still visible when collapsed — so it never moves or remounts. */}
          <PanelToggle
            collapsed={collapsed}
            onToggle={onToggleCollapsed}
            // No separate ariaLabel: the toggle's accessible name is its
            // tooltip, so the two always say the same thing.
            tooltip={collapsed ? 'Show the events list' : 'Hide the events list'}
            tone="surface"
          />
          {showContent && (
            <Typography.Title level={5} style={{ margin: 0, whiteSpace: 'nowrap', ...fade }}>
              EVENTS
            </Typography.Title>
          )}
        </div>
        {showContent && (
          <div
            style={{
              flex: 1,
              minHeight: 0,
              overflowY: collapsed ? 'hidden' : 'auto',
              ...fade,
            }}
          >
            <Input
              placeholder="Search events…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              style={{ margin: `${space.sm}px 16px 16px`, width: 288 }}
              aria-label="Search events"
            />
            {ORDERED_GROUPS.map(
              (g) =>
                groups[g].length > 0 && (
                  <div key={g}>
                    <div
                      style={{
                        padding: '8px 16px',
                        fontSize: typeScale.caption,
                        fontWeight: 600,
                        color: token.colorTextTertiary,
                        letterSpacing: tracking.label,
                      }}
                    >
                      {g.toUpperCase()}
                    </div>
                    {groups[g].map((c) => (
                      <EventCard
                        key={c.id}
                        conference={c}
                        group={g}
                        selected={c.id === selectedId}
                        onClick={() => onSelect(c.id)}
                        badgeCount={badgeByConferenceId?.[c.id]}
                        badgeTooltip={badgeTooltip}
                      />
                    ))}
                  </div>
                ),
            )}
          </div>
        )}
      </div>
    </div>
  );

  if (!overlay) return rail;

  // The 48px placeholder keeps the page where it would sit beside a collapsed
  // rail; the rail itself is positioned over it and over the page.
  return (
    <div
      style={{
        position: 'relative',
        width: PANEL_COLLAPSED_WIDTH + 1,
        height: '100%',
        flexShrink: 0,
      }}
    >
      {!collapsed && (
        <div
          data-testid="event-rail-mask"
          aria-hidden
          onClick={onToggleCollapsed}
          style={{
            position: 'fixed',
            inset: 0,
            background: token.colorBgMask,
            zIndex: OVERLAY_Z_INDEX - 1,
          }}
        />
      )}
      {rail}
    </div>
  );
}
